import { useTranslation } from 'react-i18next';
import { haptic } from '../../../../lib/haptic';
import { formatAadhaar } from '../../../../lib/aadhaar';
import { DocPicker } from '../../../../components/form/DocPicker';
import { useSignup } from '../SignupContext';

const fieldCardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-card, 18px)',
  padding: '14px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  letterSpacing: '0.01em',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '16px',
  color: 'var(--color-text)',
  padding: 0,
  fontFamily: 'inherit',
  minHeight: '24px',
};

const errorStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#B91C1C',
  margin: 0,
};

export function AadhaarStep() {
  const { t } = useTranslation('auth');
  const { state, setField, setStep } = useSignup();

  const aadhaarNumber = state.aadhaarNumber;
  const aadhaarPhoto = state.aadhaarPhotoDataUrl;

  const aadhaarValid = aadhaarNumber.length === 12;
  // Optional for now — driver can submit KYC later. Inline error still shows
  // when a partial value is typed; empty values don't block progression.
  const canContinue = aadhaarNumber.length === 0 || aadhaarValid;

  const showAadhaarError = aadhaarNumber.length > 0 && !aadhaarValid;

  const onContinue = () => {
    if (!canContinue) return;
    haptic.success();
    setStep('pan');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '12px' }}>
      <div style={fieldCardStyle}>
        <label style={labelStyle} htmlFor="signup-aadhaar-number">
          {t('signupFields.aadhaarNumber')}
        </label>
        <input
          id="signup-aadhaar-number"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={formatAadhaar(aadhaarNumber)}
          placeholder={t('signupFields.aadhaarNumberPlaceholder')}
          onChange={(e) => setField('aadhaarNumber', e.target.value.replace(/\D/g, '').slice(0, 12))}
          style={{ ...inputStyle, letterSpacing: '0.08em', fontVariantNumeric: 'tabular-nums' }}
          maxLength={14}
        />
        {showAadhaarError && (
          <p style={errorStyle}>{t('signupValidation.aadhaarInvalid')}</p>
        )}
      </div>

      <div style={fieldCardStyle}>
        <DocPicker
          value={aadhaarPhoto || null}
          onChange={(dataUrl) => setField('aadhaarPhotoDataUrl', dataUrl ?? '')}
          label={t('signupFields.aadhaarPhoto')}
          accept="image/*,application/pdf"
        />
      </div>

      <button
        type="button"
        onClick={onContinue}
        disabled={!canContinue}
        style={{
          marginTop: '8px',
          width: '100%',
          minHeight: 'var(--touch-target, 52px)',
          borderRadius: 'var(--radius-pill, 9999px)',
          border: 'none',
          background: canContinue ? 'var(--color-btn-primary)' : 'var(--color-border)',
          color: canContinue ? '#fff' : 'var(--color-text-secondary)',
          fontSize: '16px',
          fontWeight: 700,
          cursor: canContinue ? 'pointer' : 'not-allowed',
          transition: 'background-color 150ms ease, color 150ms ease',
          fontFamily: 'inherit',
        }}
      >
        {t('signupActions.continue')}
      </button>
    </div>
  );
}
